<template>
  <div class="contact-form-container mt-8">
    <div 
      class="bg-white rounded-xl shadow-lg p-6 border-2"
      :style="{
        borderColor: borderColor
      }"
    >
      <h3 class="text-xl font-semibold mb-4 text-center" :style="{ color: textColor }">
        Get in Touch
      </h3>
      
      <form @submit.prevent="submitForm" class="space-y-4">
        <!-- Name Field (if enabled) -->
        <div v-if="formType === 'name_email'">
          <label class="block text-sm font-medium mb-2" :style="{ color: textColor }">
            Name <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            required
            class="w-full px-4 py-2.5 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all shadow-sm"
            placeholder="Your name"
            :style="{ color: textColor }"
          />
        </div>

        <!-- Email Field (always required) -->
        <div>
          <label class="block text-sm font-medium mb-2" :style="{ color: textColor }">
            Email <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.email"
            type="email"
            required
            class="w-full px-4 py-2.5 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all shadow-sm"
            placeholder="your.email@example.com"
            :style="{ color: textColor }"
          />
        </div>

        <!-- Message Field (optional) -->
        <div>
          <label class="block text-sm font-medium mb-2" :style="{ color: textColor }">
            Message (optional)
          </label>
          <textarea
            v-model="form.message"
            rows="4"
            class="w-full px-4 py-2.5 rounded-lg border-2 border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 transition-all shadow-sm resize-none"
            placeholder="Your message..."
            :style="{ color: textColor }"
          ></textarea>
        </div>

        <!-- Submit Button -->
        <button
          type="submit"
          :disabled="submitting || isPreview"
          class="w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
          :style="{
            backgroundColor: buttonColor,
            opacity: submitting ? 0.7 : 1
          }"
        >
          <span v-if="!submitting">Send Message</span>
          <span v-else class="flex items-center justify-center gap-2">
            <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Sending...
          </span>
        </button>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContactForm',
  props: {
    profileId: {
      type: Number,
      required: true
    },
    formType: {
      type: String,
      default: 'email_only', // 'email_only' or 'name_email'
      validator: (value) => ['email_only', 'name_email'].includes(value)
    },
    buttonColor: {
      type: String,
      default: '#17803d'
    },
    textColor: {
      type: String,
      default: '#000000'
    },
    borderColor: {
      type: String,
      default: '#e5e7eb'
    }
  },
  data() {
    return {
      submitting: false,
      form: {
        name: '',
        email: '',
        message: ''
      }
    }
  },
  computed: {
    isPreview() {
      // If profileId is 999, it's a preview (from generator)
      return this.profileId === 999;
    }
  },
  methods: {
    async submitForm() {
      // Don't submit in preview mode
      if (this.isPreview) {
        this.showSuccessToast();
        return;
      }

      this.submitting = true;
      
      try {
        const response = await this.axios.post(this.route('leads.store'), {
          profile_id: this.profileId,
          name: this.formType === 'name_email' ? this.form.name : null,
          email: this.form.email,
          message: this.form.message || null
        });
        
        if (response.data && response.data.success) {
          // Reset form
          this.form = {
            name: '',
            email: '',
            message: ''
          };
          
          // Show success toast
          this.showSuccessToast();
        } else {
          this.showErrorToast();
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        this.showErrorToast();
      } finally {
        this.submitting = false;
      }
    },
    showSuccessToast() {
      // Create a beautiful success toast notification
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-green-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-slide-in';
      toast.innerHTML = `
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
        </svg>
        <span class="font-semibold">Message sent successfully!</span>
      `;
      
      document.body.appendChild(toast);
      
      // Remove toast after 4 seconds
      setTimeout(() => {
        toast.style.animation = 'slide-out 0.3s ease-out';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 4000);
    },
    showErrorToast() {
      const toast = document.createElement('div');
      toast.className = 'fixed top-4 right-4 z-50 bg-red-500 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 animate-slide-in';
      toast.innerHTML = `
        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
        <span class="font-semibold">Failed to send message. Please try again.</span>
      `;
      
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.style.animation = 'slide-out 0.3s ease-out';
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 300);
      }, 4000);
    }
  }
}
</script>

<style scoped>
@keyframes slide-in {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes slide-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
}

.animate-slide-in {
  animation: slide-in 0.3s ease-out;
}

.contact-form-container {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
